import inject from '@rover/utilities/injector';
import uuid from 'uuid';

import _ from '@rover/globals/underscore';
import $ from '@rover/globals/jquery';
import Backbone from '@rover/globals/backbone';
import dataLayer from '@rover/globals/dataLayer';
import { get } from 'lodash';

const useAloomaSearchUuid = inject('Rover.context.useAloomaSearchUuid', false);
const isAloomaWrapperEnabled = inject('Rover.context.isAloomaWrapperEnabled', false);

export default Backbone.Model.extend({
  // The SearchResults model contains all of the information about the
  // result of a search including metadata such as pagination info as
  // well as the individual sitter card results.  There is only one
  // instance of this model on the page.

  initialize() {
    this.allResultsCollection = new Backbone.Collection(this.getAllResults());

    // Update the results collection on change
    this.on('change', _.bind(this.updateAllResultsCollection, this));
  },

  updateAllResultsCollection() {
    this.allResultsCollection.reset(this.getAllResults());
    this.fireNewSearchEvent();
  },

  fireNewSearchEvent() {
    const query = this.get('query');
    const uuid4 = uuid.v4();

    const eventData = {
      event: 'search-page-search',
      zipCode: query.location || '',
      serviceType: query.service_type || '',
      dropOffDate: query.start_date || '',
      pickUpDate: query.end_date || '',
      priceRangeMin: query.minprice || '',
      priceRangeMax: query.maxprice || '',
      numberOfDogs: query.pet.length,
      numberOfVisitsPerDay: query.spaces_required || '',
      dogSize: this.getDogSizes(),
      serviceDays: this.getServiceDays(),
      serviceTimes: this.getServiceTimes(),
      filtersSelected: this.getFiltersSelected(),
    };

    if (useAloomaSearchUuid) {
      eventData.aloomaWrapperUuid = uuid4;
    }
    dataLayer.push(eventData);

    dataLayer.push({
      ...eventData,
      event: 'experiment-search-page-search',
    });

    if (isAloomaWrapperEnabled) {
      dataLayer.push({
        ...eventData,
        event: 'wrapped-search-page-search',
      });
    }
  },

  // GAU Event Helper methods
  getDogSizes() {
    const query = this.get('query');
    const sizes = {
      small: query.small_dogs,
      medium: query.medium_dogs,
      large: query.large_dogs,
      giant: query.giant_dogs,
    };

    return Object.keys(sizes)
      .filter(size => sizes[size])
      .join(', ');
  },

  getServiceDays() {
    const query = this.get('query');
    const daysOfWeek = {
      monday: query.monday,
      tuesday: query.tuesday,
      wednesday: query.wednesday,
      thursday: query.thursday,
      friday: query.friday,
      saturday: query.saturday,
      sunday: query.sunday,
    };

    return Object.keys(daysOfWeek)
      .filter(day => daysOfWeek[day])
      .join(', ');
  },

  getServiceTimes() {
    const query = this.get('query');
    const times = {
      fulltime_availability: query.fulltime_availability,
      midday_availability: query.midday_availability,
      morning_availability: query.morning_availability,
      evening_availability: query.evening_availability,
    };

    return Object.keys(times)
      .filter(time => times[time])
      .join(', ');
  },

  getFiltersSelected() {
    const filters = $('.js-search-advanced-filtering-view').find(
      '.js-search-applied-filter-pill:not(.hidden)'
    );
    const selectedFilters = _.map(filters, el => el.dataset.fieldName);
    return selectedFilters.join(', ');
  },
  // End GAU Event Helpers

  getAllResults() {
    const query = this.get('query');
    if (!query) {
      return [];
    }

    const results = this.getResultsForType(query.service_type);
    const altResults = this.getResultsForType(this.get('alternateType'));
    return results.concat(altResults);
  },

  getResultsForType(serviceType) {
    const query = this.get('query');
    if (!query) {
      return [];
    }

    const results =
      query.service_type === serviceType ? this.get('results') : this.get('alternate_results');
    if (!results) {
      return [];
    }

    return results;
  },

  getOffsetForType(serviceType) {
    const query = this.get('query');
    if (!query) {
      return 0;
    }

    if (query.service_type === serviceType) {
      return 0;
    }

    return this.get('result_count');
  },

  /**
   * Safely get the property of `attributes` at the end of the path,
   * returning the optional default if it does not exist.
   *
   * @example
   * const currencySymbol = searchResults.safeGet('derived_data.currency_symbol', '$');
   *
   * @see lodash#get
   *
   * @param {string} path A dot-delimited path to the property
   * @param {?any} def A default value if the path is empty; defaults to undefined
   * @returns {typeof def | any | undefined} The property at
   * the end of the path or the `def`
   */
  safeGet(path, def = undefined) {
    return get(this.attributes, path, def);
  },
});



// WEBPACK FOOTER //
// ./js/backbone/models/search/search_results.js